// Artifact validator — the deterministic Contract v2 checks (docs/ARTIFACTS_PLAN.md §9). Every generated
// document gets checked before we accept it: milliseconds, no model involved. Findings split into REJECTS
// (structure broken, worth one retry with the violation named in the prompt) and FLAGS (taste rules:
// accept the artifact, record the violation).
//
// Pure by contract: string in, findings out. No DOM APIs (runs in browser and node), never throws and
// never hangs — every check is linear regex/string work with bounded character classes, indexOf slicing
// and iteration guards, and each check runs inside its own catch so one weird document degrades to fewer
// findings, never to an exception. CSS attribution (which selector owns an animation) is best effort:
// when the CSS is too tangled to attribute, the motion checks err toward NOT flagging.

import { parseArtifactManifest } from './artifactMeta'

/** Validation outcome. Each entry is a short human-readable message naming the broken rule and, where
 *  useful, the offending value. `rejects` = structure broken, retry the generation; `flags` = taste
 *  rules, accept but record. */
export interface ArtifactValidation {
  rejects: string[]
  flags: string[]
}

/** Opening tag of the manifest script (same shape artifactMeta parses), used for presence detection. */
const MANIFEST_TAG_RE =
  /<script\b[^>]*\bid\s*=\s*(?:"artifact-manifest"|'artifact-manifest'|artifact-manifest(?=[\s>/]))[^>]*>/i

/** Runs one check, swallowing anything it throws: a weird document must never take the validator down. */
function attempt(check: () => void): void {
  try {
    check()
  } catch {
    // degrade to fewer findings, never to a throw
  }
}

function dedupe(list: string[]): string[] {
  return Array.from(new Set(list))
}

/**
 * Where the product keeps every stored picture. `StoragePathConfig.buildStorageUrl` always answers this
 * path, root-relative, and the frontend has no host of its own for it: the Vite proxy and prod nginx both
 * forward `/api`. So a stored URL is known by PATH, with or without an origin in front, exactly as
 * `stripOrigin` in kitSite compares KIT ASSETS: a listed asset written relative or absolute is the same
 * asset, and there is no host string here to drift.
 */
export const STORED_IMAGE_PATH = '/api/v1/storage/'

/** A URL into the product's own image storage: a KIT ASSETS or DECK IMAGES picture. With a verified mark
 *  and a data: URI, the only `src` an artifact's <img> may carry. */
export function isStoredImageUrl(src: string): boolean {
  if (typeof src !== 'string') return false
  const path = src.trim().replace(/^https?:\/\/[^/]+/i, '')
  return path.startsWith(STORED_IMAGE_PATH) && path.length > STORED_IMAGE_PATH.length
}

/** Where `resolveArtifactLogos` fills a verified mark from (deckgen-image-search-api). A src on this host is
 *  the system's own answer to a data-logo declaration, never something the writer composed. */
const VERIFIED_LOGO_HOST_RE = /^https:\/\/img\.logo\.dev\//i

/** One attribute's value off a single tag, or null when the tag does not carry it. Anchored on the
 *  whitespace before the name so `src` never reads `data-src`, and `srcset` never reads as `src`. */
function attrOf(tag: string, name: string): string | null {
  const re = new RegExp(`\\s${name}\\s*=\\s*(?:"([^"]*)"|'([^']*)'|([^\\s>"']+))`, 'i')
  const m = re.exec(tag)
  return m ? (m[1] ?? m[2] ?? m[3] ?? '') : null
}


function clipUrl(src: string): string {
  return src.length > 80 ? src.slice(0, 79) + '…' : src
}

/** The first `:root` block, by index. Duplicated from artifactMeta (kept private there) — ten lines of
 *  indexOf beats coupling the validator to a helper the meta module does not export. */
function firstRootBlock(html: string): { start: number; end: number; body: string } | null {
  const rootIdx = html.indexOf(':root')
  if (rootIdx < 0) return null
  const open = html.indexOf('{', rootIdx)
  if (open < 0) return null
  const close = html.indexOf('}', open + 1)
  if (close < 0) return null
  return { start: open + 1, end: close, body: html.slice(open + 1, close) }
}

function extractStyleBlocks(html: string): string[] {
  const out: string[] = []
  const re = /<style\b[^>]*>([\s\S]*?)<\/style\s*>/gi
  let m: RegExpExecArray | null
  let guard = 0
  while ((m = re.exec(html)) !== null && guard++ < 200) out.push(m[1])
  return out
}

/**
 * THE CSS OF A DOCUMENT, and nothing else: its `<style>` blocks (one left open runs to the end, as a browser
 * reads it), its scripts (a script can write CSS), and every `style=""` attribute. Never its words, comments or
 * other attributes. Text a user typed into a caret or pasted ("font-size: 2rem", an alt of "overflow: auto") is
 * not a declaration, and a rule that read it refused every later AI edit of that artifact (2026-09-27).
 *
 * A linear scan like the rest: each alternative starts with its own character, a quoted attribute value is
 * skipped whole so a `>` inside it does not end the tag, and the loop is bounded.
 */
function cssOf(doc: string): string {
  const parts: string[] = []
  const re =
    /<(style|script)\b[^>]*>([\s\S]*?)(?:<\/\1\s*>|$)|<!--[\s\S]*?(?:-->|$)|<[a-zA-Z][^\s/>"']*((?:[^>"']|"[^"]*"|'[^']*')*)>/gi
  const styleAttr = /(?:^|\s)style\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'>]+))/gi
  let m: RegExpExecArray | null
  let guard = 0
  while ((m = re.exec(doc)) !== null && guard++ < 50000) {
    if (m[1]) {
      parts.push(m[2])
      continue
    }
    const attrs = m[3]
    if (!attrs) continue
    styleAttr.lastIndex = 0
    let a: RegExpExecArray | null
    let inner = 0
    while ((a = styleAttr.exec(attrs)) !== null && inner++ < 200) parts.push(a[1] ?? a[2] ?? a[3] ?? '')
  }
  return parts.join('\n')
}

/**
 * Flat brace walk over a stylesheet: selector = text since the previous `}`, body = text to the next `}`.
 * Nested at-rules (`@media`, `@keyframes`) come out imperfect — a swallowed inner selector lands in the
 * body — which the motion checks tolerate by testing selector AND body for `data-ambient`.
 */
function walkRules(css: string): Array<{ selector: string; body: string }> {
  const rules: Array<{ selector: string; body: string }> = []
  let pos = 0
  for (let guard = 0; guard < 5000; guard++) {
    const open = css.indexOf('{', pos)
    if (open < 0) break
    const close = css.indexOf('}', open + 1)
    if (close < 0) break
    rules.push({ selector: css.slice(pos, open), body: css.slice(open + 1, close) })
    pos = close + 1
  }
  return rules
}

/** Every `Ns`/`Nms` time in the text, in milliseconds. A leading-boundary group (instead of lookbehind,
 *  for older engines) keeps `2s` inside a keyframe name like `fade2s` from parsing as a time. */
function parseTimesMs(text: string): number[] {
  const out: number[] = []
  const re = /(^|[^A-Za-z0-9_.#-])(\d*\.?\d+)\s*(ms|s)(?![A-Za-z0-9])/gi
  let m: RegExpExecArray | null
  let guard = 0
  while ((m = re.exec(text)) !== null && guard++ < 200) {
    const n = Number.parseFloat(m[2])
    if (!Number.isFinite(n)) continue
    out.push(m[3].toLowerCase() === 's' ? n * 1000 : n)
  }
  return out
}

interface RuleMotion {
  ambient: boolean
  /** Every non-zero animation duration seen in the rule (band check). */
  durations: number[]
  /** Best-effort worst-case delay+duration for the rule (settle law). */
  settleMs: number
  infinite: boolean
}

/** Reads the animation story of one rule from its declarations. Returns null when the body has none. */
function scanRuleMotion(selector: string, body: string): RuleMotion | null {
  if (!/animation/i.test(body)) return null
  const ambient = /data-ambient/i.test(selector) || /data-ambient/i.test(body)
  const shorthand: Array<{ duration: number; delay: number }> = []
  const longhandDurations: number[] = []
  const longhandDelays: number[] = []
  let infinite = false
  const declRe = /\banimation(-[a-z]+(?:-[a-z]+)*)?\s*:\s*([^;}{]*)/gi
  let m: RegExpExecArray | null
  let guard = 0
  while ((m = declRe.exec(body)) !== null && guard++ < 500) {
    const prop = (m[1] ?? '').toLowerCase()
    const value = m[2]
    if (prop === '') {
      // Shorthand, possibly comma-separated multi-animation. Parens are blanked first so cubic-bezier
      // commas do not split the list; per CSS the first time is the duration, the second the delay.
      if (/\binfinite\b/i.test(value)) infinite = true
      for (const part of value.replace(/\([^()]*\)/g, ' ').split(',')) {
        const times = parseTimesMs(part)
        shorthand.push({ duration: times[0] ?? 0, delay: times[1] ?? 0 })
      }
    } else if (prop === '-duration') {
      longhandDurations.push(...parseTimesMs(value))
    } else if (prop === '-delay') {
      longhandDelays.push(...parseTimesMs(value))
    } else if (prop === '-iteration-count') {
      if (/\binfinite\b/i.test(value)) infinite = true
    }
  }
  const durations = [...shorthand.map((p) => p.duration), ...longhandDurations].filter((d) => d > 0)
  // Settle: per-shorthand pairs are exact; longhands pair with the rule's worst counterpart.
  const settle: number[] = shorthand.map((p) => p.duration + p.delay)
  const maxShortDur = Math.max(0, ...shorthand.map((p) => p.duration))
  const maxShortDelay = Math.max(0, ...shorthand.map((p) => p.delay))
  const maxLongDur = Math.max(0, ...longhandDurations)
  const maxLongDelay = Math.max(0, ...longhandDelays)
  if (longhandDelays.length > 0) settle.push(Math.max(maxShortDur, maxLongDur) + maxLongDelay)
  if (longhandDurations.length > 0) settle.push(maxLongDur + Math.max(maxShortDelay, maxLongDelay))
  return { ambient, durations, settleMs: Math.max(0, ...settle), infinite }
}

/** Selector text made printable for a flag message. */
function clipSelector(selector: string): string {
  const clean = selector.replace(/[{}]/g, ' ').replace(/\s+/g, ' ').trim()
  if (clean === '') return 'unattributed rule'
  return clean.length > 48 ? clean.slice(0, 47) + '…' : clean
}

function fmtMs(ms: number): string {
  return `${Math.round(ms)}ms`
}

/** A CSS value holding a non-zero length or number, colours aside (`border: 0 solid #333` holds none). */
function hasLength(value: string): boolean {
  const bare = value
    .replace(/#[0-9a-f]{3,8}\b/gi, ' ')
    .replace(/\b(?:rgba?|hsla?|oklch|oklab|lab|lch|color|var)\([^)]*\)/gi, ' ')
  return (bare.match(/-?\d*\.?\d+/g) ?? []).some((n) => Number(n) !== 0)
}

/**
 * The deterministic Contract v2 validator. REJECTS mean the structure is broken and the generation is
 * worth one retry (send {@link formatViolationsForPrompt} back to the model); FLAGS are taste rules —
 * accept the artifact, log the violation. Never throws, never hangs; on an unparseable document the
 * findings are best effort.
 */
export function validateArtifact(html: string): ArtifactValidation {
  const rejects: string[] = []
  const flags: string[] = []
  const doc = typeof html === 'string' ? html : ''
  const lower = doc.toLowerCase()
  const manifest = parseArtifactManifest(doc)
  // What REJECTS 4 and 5 read: the CSS, never the words (see cssOf).
  let css = ''
  attempt(() => {
    css = cssOf(doc)
  })

  // REJECT 1 — a complete document, doctype to close.
  attempt(() => {
    if (!/^\s*<!doctype\s+html/i.test(doc)) rejects.push('Document does not start with <!doctype html>')
    if (!/<\/html\s*>/i.test(doc)) rejects.push('Document has no closing </html>')
  })

  // REJECT 2 — a <style> block in <head>. Without a findable </head> the whole document is the region,
  // erring toward acceptance on tag-soup rather than a false structural reject.
  attempt(() => {
    const headEnd = lower.indexOf('</head')
    const region = headEnd >= 0 ? doc.slice(0, headEnd) : doc
    if (!/<style\b/i.test(region)) rejects.push('No <style> block in <head>')
  })

  // REJECT 3 — manifest present but broken, or wrong contract. FLAG 1 — manifest missing entirely
  // (older artifacts are legal; new ones should carry it).
  attempt(() => {
    const present = MANIFEST_TAG_RE.test(doc)
    if (present && manifest === null) {
      rejects.push('Manifest block is not valid JSON with a numeric "contract"')
    } else if (manifest && manifest.contract !== 2) {
      rejects.push(`Manifest contract is ${manifest.contract}, expected 2`)
    } else if (!present) {
      flags.push('No artifact manifest block')
    }
  })

  // REJECT 4 — font sizes in relative viewport-ish units. The artifact canvas is a fixed 1920x1080.
  attempt(() => {
    const seen: string[] = []
    const re = /font-size\s*:\s*([^;}"']*)/gi
    let m: RegExpExecArray | null
    let guard = 0
    while ((m = re.exec(css)) !== null && guard++ < 2000) {
      const value = m[1].trim()
      if (/\d\s*(rem|vw|vh)(?![A-Za-z0-9])/i.test(value) || value.includes('%')) {
        if (!seen.includes(value)) seen.push(value)
      }
    }
    if (seen.length > 0) {
      const shown = seen
        .slice(0, 3)
        .map((v) => `"${v}"`)
        .join(', ')
      const more = seen.length > 3 ? ` and ${seen.length - 3} more` : ''
      rejects.push(`font-size in forbidden units (rem/vw/vh/%): ${shown}${more}`)
    }
  })

  // REJECT 5 — overflow: auto|scroll in any CSS (also the -x/-y variants). Slides do not scroll.
  attempt(() => {
    const re = /overflow(?:-[xy])?\s*:\s*([^;}"']*)/gi
    let m: RegExpExecArray | null
    let guard = 0
    while ((m = re.exec(css)) !== null && guard++ < 2000) {
      if (/\b(auto|scroll)\b/i.test(m[1])) {
        rejects.push(`overflow auto/scroll is forbidden: "${m[0].replace(/\s+/g, ' ').trim()}"`)
      }
    }
  })

  // REJECT 6 — size cap.
  attempt(() => {
    if (doc.length > 60000) rejects.push(`Document is ${doc.length} characters, over the 60000 cap`)
  })

  // REJECT 7, pictures. An <img> may point at the product's own storage (a KIT ASSETS or DECK IMAGES
  // URL), at the verified mark the logo pass filled, or at a data: URI; every other remote src is a
  // picture the contract opened no door for. A srcless data-logo declaration is the contract's own.
  // REJECT 8, a placed picture (data-deck-image) points at storage — the exact stored URL or its bytes —
  // and nothing else. It is NOT required to sit in a <figure data-slot="image-*"> any more.
  //
  // It used to be, and the reason it stopped is that the rule outlived its tool. Slots, REJECT 8 and
  // `place_image` were one system: the image tool owned every slot, so a picture anywhere else had been
  // placed by hand. Then `place_image` came off the chat agent's menu ("pictures inside an artifact go
  // through the generic read/edit/look tools", 2026-09), and `read_artifact` began telling the agent to
  // put a picture "wherever the layout wants it (a tile, a column, a figure)". The agent did exactly that,
  // this rule refused it with "place images with the image tool", and an agent with no image tool
  // concluded pictures could not be placed at all (Beka, 2026-09-23: "agent can't put the image inside
  // the artifact"). Nothing at runtime reads slots — the preview, the bake and the measurement
  // (`look_at_artifact` reports each picture's container and whether it clips) all work on any container.
  //
  // What stays is the rule that protects the user: a placed picture is one of THEIR stored pictures,
  // never an arbitrary URL. The full contract still steers a writer that HAS the image tool into slots;
  // it simply is no longer a rejection when a writer without one lays the picture out itself.
  attempt(() => {
    const imgRe = /<img\b[^>]*>/gi
    let m: RegExpExecArray | null
    let guard = 0
    while ((m = imgRe.exec(doc)) !== null && guard++ < 500) {
      const tag = m[0]
      const src = attrOf(tag, 'src')
      const deckImage = attrOf(tag, 'data-deck-image')
      if (deckImage !== null) {
        const id = deckImage.trim() || '(no id)'
        // The image tool inlines the stored bytes as a data: URI, because a sandboxed frame on an opaque
        // origin cannot fetch the storage route (measured 2026-09-07: localhost never, prod 401 without
        // the cookie). So a placed picture is either the exact stored URL or its bytes, never anything else.
        if (src !== null && !isStoredImageUrl(src) && !/^data:image\//i.test(src.trim())) {
          rejects.push(
            `Image ${id} points at "${clipUrl(src.trim())}", which is not a stored picture; use the exact URL from the DECK IMAGES block`,
          )
        }
        continue
      }
      if (src === null) continue
      const value = src.trim()
      if (value === '' || /^data:/i.test(value)) continue
      const remote = /^(?:https?:)?\/\//i.test(value) || value.startsWith('/')
      if (!remote) continue
      if (isStoredImageUrl(value) || VERIFIED_LOGO_HOST_RE.test(value)) continue
      rejects.push(
        `Remote image "${clipUrl(value)}" is forbidden; a document may place only a verified logo declaration, a KIT ASSETS URL or a DECK IMAGES URL`,
      )
    }
  })

  // FLAG 2 — the budget comment, first thing in <body> (within its first 200 characters).
  attempt(() => {
    const bodyIdx = lower.indexOf('<body')
    let ok = false
    if (bodyIdx >= 0) {
      const windowText = lower.slice(bodyIdx + 5, bodyIdx + 5 + 200)
      const commentIdx = windowText.indexOf('<!--')
      ok = commentIdx >= 0 && windowText.indexOf('budget', commentIdx) >= 0
    }
    if (!ok) flags.push('No budget comment at the top of <body>')
  })

  // FLAG 3 — the tokens discipline: a :root block, and no raw hex color literals after it. Hex inside
  // data: URIs and inside the manifest script does not count.
  attempt(() => {
    const root = firstRootBlock(doc)
    if (!root) {
      flags.push('No :root token block')
      return
    }
    let tail = doc.slice(root.end + 1)
    tail = tail.replace(new RegExp(MANIFEST_TAG_RE.source + '[\\s\\S]*?<\\/script', 'i'), ' ')
    tail = tail.replace(/data:[^"'()\s]*/gi, ' ')
    const hexRe = /#(?:[0-9a-f]{8}|[0-9a-f]{6}|[0-9a-f]{4}|[0-9a-f]{3})(?![0-9a-z])/gi
    let count = 0
    let guard = 0
    while (hexRe.exec(tail) !== null && guard++ < 5000) count++
    if (count > 0) flags.push(`${count} raw colors outside tokens`)
  })

  // FLAGS 4, 5, 6 — motion bands, the 1200ms settle law, and infinite outside data-ambient. Rule-level
  // best effort: rules whose selector (or tangled body) mentions data-ambient are exempt from all three.
  attempt(() => {
    for (const css of extractStyleBlocks(doc)) {
      for (const rule of walkRules(css)) {
        const motion = scanRuleMotion(rule.selector, rule.body)
        if (!motion || motion.ambient) continue
        const sel = clipSelector(rule.selector)
        for (const d of motion.durations) {
          if (d < 400 || d > 700) flags.push(`Entrance duration ${fmtMs(d)} outside the 400-700ms band (${sel})`)
        }
        if (motion.settleMs > 1200) {
          flags.push(`Animation settles at ${fmtMs(motion.settleMs)}, past the 1200ms law (${sel})`)
        }
        if (motion.infinite) flags.push(`Infinite animation outside data-ambient (${sel})`)
      }
    }
  })

  // FLAG 7 — the reduced-motion escape hatch.
  attempt(() => {
    if (!lower.includes('prefers-reduced-motion')) flags.push('No prefers-reduced-motion media query')
  })

  // FLAG 9 — runtime hygiene. An unknown deck-* tag is a typo that renders as an empty inline element,
  // and a data-reveal value the runtime does not implement silently does nothing; both are worth a line
  // in the log. Keep the known set in lockstep with deckRuntime.ts.
  attempt(() => {
    const known = new Set(['deck-stat', 'deck-quote', 'deck-list'])
    const re = /<deck-([a-z][a-z0-9-]*)/gi
    let m: RegExpExecArray | null
    let guard = 0
    const bad = new Set<string>()
    while ((m = re.exec(doc)) !== null && guard++ < 500) {
      const tag = 'deck-' + m[1].toLowerCase()
      if (!known.has(tag)) bad.add(tag)
    }
    for (const t of bad) flags.push(`Unknown deck component <${t}>`)
  })
  attempt(() => {
    const re = /\bdata-reveal\s*=\s*["']?([a-z-]+)/gi
    let m: RegExpExecArray | null
    let guard = 0
    while ((m = re.exec(doc)) !== null && guard++ < 200) {
      const value = m[1].toLowerCase()
      if (value !== 'words') flags.push(`data-reveal="${value}" is not implemented (only "words")`)
    }
  })

  // FLAG 8 — manifest steps vs the distinct data-step values actually present in the markup. Style
  // blocks and the manifest are stripped first so CSS selectors like [data-step="2"] do not count.
  attempt(() => {
    if (!manifest || typeof manifest.steps !== 'number' || !Number.isFinite(manifest.steps)) return
    let markup = doc.replace(/<style\b[^>]*>[\s\S]*?<\/style\s*>/gi, ' ')
    markup = markup.replace(new RegExp(MANIFEST_TAG_RE.source + '[\\s\\S]*?<\\/script', 'i'), ' ')
    const values = new Set<string>()
    const re = /\bdata-step\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>"'/]+))/gi
    let m: RegExpExecArray | null
    let guard = 0
    while ((m = re.exec(markup)) !== null && guard++ < 5000) {
      values.add((m[1] ?? m[2] ?? m[3] ?? '').trim())
    }
    if (values.size !== manifest.steps) {
      flags.push(
        `Manifest declares ${manifest.steps} steps but the document has ${values.size} distinct data-step values`,
      )
    }
  })

  // FLAG 10 — html with a box of its own. The frame is body: the bake draws body alone, and the live frame
  // takes html's margin, padding and border away (docBridge's hygiene style), but a padding written into
  // one shared `html, body` rule still pads twice wherever the document is opened as it is written (Beka,
  // 2026-09-26: a whole deck sat shifted right and down). A flag, not a reject: every edit of a document
  // already written this way would otherwise be refused for a fault the edit did not make.
  attempt(() => {
    for (const block of extractStyleBlocks(doc)) {
      for (const { selector, body } of walkRules(block)) {
        // The flat walk can hand back a stray brace from a closed @keyframes or @media, or a comment.
        const sel = selector.replace(/\/\*[\s\S]*?\*\//g, ' ').replace(/[{}]/g, ' ')
        if (!sel.split(',').some((part) => /^(?:html|:root)$/i.test(part.trim()))) continue
        const re = /(?:^|;)\s*((?:padding|margin|border)(?:-(?:top|right|bottom|left))?(?:-width)?)\s*:\s*([^;]*)/gi
        let m: RegExpExecArray | null
        let guard = 0
        while ((m = re.exec(body)) !== null && guard++ < 200) {
          if (!hasLength(m[2])) continue
          flags.push(
            `html has a box of its own ("${m[1]}: ${m[2].trim()}" in ${clipSelector(selector)}): the frame is body, so padding, margin and borders go on body alone`,
          )
          break
        }
      }
    }
  })

  return { rejects: dedupe(rejects), flags: dedupe(flags) }
}

/** The rejects as short imperative lines, ready to hand back to the model on the retry. */
/**
 * The rejects a hand write would ADD (2026-09-27): a paste or a duplicate that makes the document one every later
 * AI edit is refused on (over the size cap, or a copy whose own inline style breaks a rule). Words never do: the
 * rules read CSS only (cssOf).
 * What the document already broke is not this write's; the size cap counts once however long it grows.
 */
export function addedRejects(before: string, after: string): string[] {
  const key = (reject: string) => (/^Document is \d+ characters/.test(reject) ? 'Document over the size cap' : reject)
  const had = validateArtifact(before).rejects.map(key)
  const out: string[] = []
  for (const reject of validateArtifact(after).rejects) {
    const i = had.indexOf(key(reject))
    if (i >= 0) had.splice(i, 1)
    else out.push(reject)
  }
  return out
}

export function formatViolationsForPrompt(v: ArtifactValidation): string[] {
  try {
    if (!v || !Array.isArray(v.rejects)) return []
    return v.rejects.filter((r): r is string => typeof r === 'string').map((r) => `Fix: ${r}`)
  } catch {
    return []
  }
}
